import { useEffect, useState } from "react";
import { useTenant } from "@/lib/tenants/tenantContext";

/**
 * InfluencerTakeoverMode
 * Resolves an active takeover from (in order):
 *   1. URL ?influencer=slug (instant preview for creators)
 *   2. Tenant config influencer_name
 * Returns a normalized config used by the takeover banner, sponsor moments,
 * and creator camera frame.
 */
export type TakeoverConfig = {
  active: boolean;
  slug: string;
  name: string;
  slogan: string;
  emoji: string;
  accent: string; // hex
  challengeLabel?: string;
  nationCode?: string | null;
  logoUrl?: string | null;
};

const PRESETS: Record<string, Omit<TakeoverConfig, "active" | "slug">> = {
  mexico: {
    name: "La Nación Mexicana",
    slogan: "🔥 La Nación Mexicana entra al Mundial",
    emoji: "🇲🇽",
    accent: "#1B8A2C",
    challengeLabel: "Domina el hype mundial",
    nationCode: "MEX",
  },
  brasil: {
    name: "Brasil Hype Squad",
    slogan: "🏆 Brasil quiere dominar el hype global",
    emoji: "🇧🇷",
    accent: "#FFCC29",
    challengeLabel: "Supera a Brasil",
    nationCode: "BRA",
  },
  argentina: {
    name: "Argentina Campeón",
    slogan: "⚽ Vamos Argentina, conquista el mundo",
    emoji: "🇦🇷",
    accent: "#75AADB",
    challengeLabel: "Predice como un campeón",
    nationCode: "ARG",
  },
  // Brand removed: no active contract. Placeholder slot kept for routing compatibility.
  patrocinio: {
    name: "Patrocinio Disponible",
    slogan: "🎙️ Powered by VerseSport Intelligence",
    emoji: "🎙️",
    accent: "#E94560",
    challengeLabel: "Challenge VerseSport",
  },
};

function fromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const sp = new URLSearchParams(window.location.search);
  return sp.get("influencer");
}

export function resolveTakeover(slug: string | null, fallbackName?: string | null): TakeoverConfig {
  const key = (slug ?? "").toLowerCase().trim();
  if (key && PRESETS[key]) {
    return { active: true, slug: key, ...PRESETS[key] };
  }
  if (fallbackName) {
    return {
      active: true,
      slug: fallbackName.toLowerCase().replace(/\s+/g, "-"),
      name: fallbackName,
      slogan: `🎙️ ${fallbackName} te lleva al Mundial`,
      emoji: "🎙️",
      accent: "#E94560",
      challengeLabel: `Challenge by ${fallbackName}`,
    };
  }
  return {
    active: false, slug: "", name: "", slogan: "", emoji: "✨",
    accent: "#E94560",
  };
}

export function useTakeover(): TakeoverConfig {
  const { tenant } = useTenant();
  const [urlSlug, setUrlSlug] = useState<string | null>(null);
  useEffect(() => { setUrlSlug(fromUrl()); }, []);
  return resolveTakeover(urlSlug, tenant.influencer_name);
}
